import { BoardFileError, figmaToBoardFile, type BoardFile, type FigmaColors, type FigmaImportReport } from '@os/doc';
import { parseFigmaUrl } from '@os/shared';
import { FigmaImportError, type Api } from '../../../lib/api';
import { DEFAULT_STICKY, nearestColor, OBJECT_COLORS } from '../../../ui/palette';
import { SECTION_COLORS } from '../../../ui/section-colors';
import type { BoardArchive } from '../../export/archive';

const STICKIES = OBJECT_COLORS.map((c) => c.hex);
const SECTIONS = SECTION_COLORS.map((c) => c.fill);

/** FigJam colours as the studio's: the nearest sticky swatch and section fill (plan P11b Ruling 8). */
export function figmaColors(now: Date = new Date()): FigmaColors {
  return {
    stickyColor: (hex) => (hex ? nearestColor(hex, STICKIES) : DEFAULT_STICKY),
    sectionFill: (hex) => (hex ? nearestColor(hex, SECTIONS) : SECTION_COLORS[0].fill),
    exportedAt: now.toISOString(),
  };
}

export type FigmaRead = { name: string; file: BoardFile; report: FigmaImportReport };

/**
 * Reads a pasted FigJam link through the Worker and converts it (backlog §12). The token is passed on once and not
 * kept anywhere by this module; the caller clears its field.
 */
export async function readFigma(api: Pick<Api, 'figmaFile'>, input: { boardId: string; url: string; token: string }, signal?: AbortSignal): Promise<FigmaRead> {
  const key = parseFigmaUrl(input.url);
  if (!key) throw new FigmaImportError('invalid');
  const source = await api.figmaFile({ boardId: input.boardId, fileKey: key.fileKey, token: input.token.trim() }, signal);
  try {
    const { file, report } = figmaToBoardFile(source, figmaColors());
    return { name: source.name, file, report };
  } catch (err) {
    if (err instanceof BoardFileError) throw new FigmaImportError('figma_too_large');
    throw err;
  }
}

/** A converted file as the P9 import's input: nothing read from disk, images fetched by URL. */
export const archiveOf = (file: BoardFile): BoardArchive => ({ file, skipped: 0, blobs: new Map(), missing: [] });

/** Image fills downloaded through the Worker's relay (Figma's hosts refuse the browser, spike). */
export const figmaFetch = (api: Pick<Api, 'figmaImage'>, boardId: string, signal?: AbortSignal) => (url: string) => api.figmaImage(boardId, url, signal);
